// Email templates for the church communication system
export const emailTemplates = [
  {
    id: 'prayer_request',
    name: 'Prayer Request',
    description: 'Share prayer requests with the congregation',
    subject: 'Prayer Request: {prayer_for}',
    template: `Dear Family,</br></br>

🙏 PRAYER REQUEST</br></br>

For: {prayer_for}</br>
Request: {prayer_request}</br></br>

{additional_details}</br></br>

"For where two or three gather in my name, there am I with them." - Matthew 18:20</br></br>

Thank you for your prayers and support.`,
    variables: ['prayer_for', 'prayer_request', 'additional_details']
  },
  {
    id: 'event_announcement',
    name: 'Event Announcement',
    description: 'Announce upcoming church events',
    subject: '{event_title} - {event_date}',
    template: `Dear Church Family,

📅 UPCOMING EVENT

Event: {event_title}
Date: {event_date}
Time: {event_time}
Location: {event_location}

{event_description}

We look forward to seeing you there!`,
    variables: ['event_title', 'event_date', 'event_time', 'event_location', 'event_description']
  },
  {
    id: 'general_alert',
    name: 'General Alert',
    description: 'Send important alerts and announcements',
    subject: 'Important: {alert_title}',
    template: `Dear Church Family,

🚨 {alert_title}

{alert_message}

Thank you for your attention to this matter.`,
    variables: ['alert_title', 'alert_message']
  },
  {
    id: 'volunteer_opportunity',
    name: 'Volunteer Opportunity',
    description: 'Recruit volunteers for church activities',
    subject: 'Volunteer Opportunity: {opportunity_title}',
    template: `Dear Church Family,

🤝 VOLUNTEER OPPORTUNITY

{opportunity_title}

{opportunity_description}

{how_to_help}

Your service makes a difference in our church community!`,
    variables: ['opportunity_title', 'opportunity_description', 'how_to_help']
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Weekly or monthly church newsletter',
    subject: '{newsletter_title} - {newsletter_date}',
    template: `Dear Church Family,

📰 {newsletter_title}
{newsletter_date}

{newsletter_content}

Thank you for being part of our church family.`,
    variables: ['newsletter_title', 'newsletter_date', 'newsletter_content']
  },
  {
    id: 'general_message',
    name: 'General Message',
    description: 'General purpose message template',
    subject: 'Message from {church_name}',
    template: `Dear Church Family,

{message_content}

Blessings,
{church_name} Team`,
    variables: ['message_content']
  }
];

// Template variables that can be used in any template
export const templateVariables = {
  church_name: 'Church Name',
  current_date: new Date().toLocaleDateString(),
  current_year: new Date().getFullYear().toString()
};

// Function to replace variables in template text
export const replaceTemplateVariables = (text, variables = {}, globalVars = templateVariables) => {
  let result = text;
  
  // Replace global variables first
  Object.entries(globalVars).forEach(([key, value]) => {
    const regex = new RegExp(`{${key}}`, 'g');
    result = result.replace(regex, value);
  });
  
  // Replace template-specific variables
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{${key}}`, 'g');
    result = result.replace(regex, value || '');
  });
  
  return result;
};

// Function to get template by ID
export const getTemplateById = (id) => {
  return emailTemplates.find(template => template.id === id);
};

// Function to render email template with full HTML
export const renderEmailTemplate = (template, variables = {}, globalVars = templateVariables) => {
  // Replace variables in the template content
  const renderedTemplate = replaceTemplateVariables(template.template || template.body || '', variables, globalVars);
  
  // Create the full HTML email template
  let htmlTemplate = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Message from {church_name}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        /* Dark mode support - more aggressive approach */
        @media (prefers-color-scheme: dark) {
            .email-content {
                background-color: #1f2937 !important;
                color: #f9fafb !important;
            }
            .email-text {
                color: #f9fafb !important;
                background-color: #1f2937 !important;
            }
            .email-footer {
                background-color: #111827 !important;
            }
        }
        
        /* Force better contrast for all clients */
        .email-text {
            color: #1f2937 !important;
            background-color: #ffffff !important;
        }
        
        /* Dark mode fallback using data attributes */
        [data-theme="dark"] .email-container {
            background-color: #1f2937 !important;
        }
        [data-theme="dark"] .email-content {
            background-color: #1f2937 !important;
        }
        [data-theme="dark"] .email-text {
            color: #f9fafb !important;
            background-color: #1f2937 !important;
        }
        [data-theme="dark"] .email-footer {
            background-color: #111827 !important;
        }
        
        /* Mobile optimizations */
        @media (max-width: 600px) {
            .email-content {
                padding: 24px 20px !important;
            }
            .email-header {
                padding: 32px 20px !important;
            }
        }
    </style>
    <script>
        // Dark mode detection for email clients
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            document.documentElement.setAttribute('data-theme', 'dark');
        }
    </script>
</head>
<body style="margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; min-height: 100vh;">
    <div class="email-container" style="max-width: 600px; margin: 0 auto; background-color: white; border-radius: 16px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);">
        <!-- Header -->
        <div class="email-header" style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); padding: 48px 40px; text-align: center; position: relative; overflow: hidden;">
            <!-- Background Pattern -->
            <!-- Brand -->
            <div style="position: relative; z-index: 2;">
                <h1 style="color: white; margin: 0; font-size: 32px; font-weight: 800; letter-spacing: -0.5px; text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">{church_name}</h1>
                <p style="color: rgba(255, 255, 255, 0.9); margin: 8px 0 0; font-size: 16px; font-weight: 500;">Connecting Our Church Family</p>
            </div>
        </div>

        <!-- Main Content -->
        <div class="email-content" style="padding: 48px 40px; background: #ffffff;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #ffffff;">
                <tr>
                    <td class="email-text" style="color: #1f2937; font-size: 16px; line-height: 1.7; font-weight: 400; background-color: #ffffff; padding: 0;">
                        ${renderedTemplate}
                    </td>
                </tr>
                <tr>
                    <td style="margin-top: 32px; padding-top: 24px; border-top: 1px solid #e5e7eb;">
                        <div style="color: #6b7280; font-size: 14px; line-height: 1.6; background-color: #ffffff;">
                            Blessings,<br>
                            {church_name} Team
                        </div>
                    </td>
                </tr>
            </table>
        </div>

        <!-- Footer -->
        <div class="email-footer" style="background: linear-gradient(135deg, #1f2937 0%, #111827 100%); padding: 40px; text-align: center; border-top: 1px solid #374151;">
            <div style="margin-bottom: 24px;">
                <div style="width: 48px; height: 48px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center;">
                    <img src="https://getdeacon.com/apple-touch-icon-167x167.png" 
                         alt="Deacon Logo" 
                         style="width: 48px; height: 48px; border-radius: 12px; object-fit: cover; display: block;" />
                </div>
                <h3 style="color: white; margin: 0 0 8px; font-size: 18px; font-weight: 700;">Deacon</h3>
                <p style="color: #9ca3af; margin: 0; font-size: 14px; font-weight: 500;">Connecting Your Church Family</p>
            </div>
            <div style="border-top: 1px solid #374151; padding-top: 24px;">
                <p style="color: #9ca3af; margin: 0 0 8px; font-size: 12px; line-height: 1.5;">
                    This email was sent from your church's communication system.
                </p>
                <p style="color: #9ca3af; margin: 0; font-size: 12px; line-height: 1.5;">
                    Questions? Contact your church administrator for assistance.
                </p>
            </div>
        </div>
    </div>
</body>
</html>
  `;
  
  // Replace global variables in the HTML template
  Object.entries(globalVars).forEach(([key, value]) => {
    const regex = new RegExp(`{${key}}`, 'g');
    htmlTemplate = htmlTemplate.replace(regex, value);
  });
  
  return htmlTemplate;
};